import React, { useState } from "react";
import {renderRatingStars, truncateString, stripHTMLFromString} from '../../utils/utilsHelpers';
import {COMPANY_NAME} from '../../constants/constants';
import Card from 'react-bootstrap/Card'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Image from 'react-bootstrap/Image';
import Button from 'react-bootstrap/Button';
import Pluralize from 'react-pluralize';
import TooltipTrigger from "../bootstrap/TooltipTrigger";
import ConditionalWrapper from "../../utils/ConditionalWrapper";

// This is currently being used on vendor_dashboards/products and the category landing pages.

function ProductCard(props) {

    let { product,
        displayInCard=true,
        showFollowers=false,
        showCategory=false,
        showDescription=false,
        showRating=false,
        showReviewCount=false,
        showFlashyStats=false,
        showBadge=false,
        showProductCta=false,
        comparisonEnabled=false,
        logoSize=60,
        nameSize=null,
        nameBlock=false,
        textTruncate=30,
        colClasses,
        renderAsLink=true,
        onProductClick=null,
        onCompareClick=null,
        productContentClasses,
        fitContent=false
    } = props;

    const [activeComparison, setActiveComparison] = useState(props.activeComparison);

    const renderFollowers = () => {
        if(showFollowers && product?.follower_count) {
            return (
                <div className={'stats'}>
                    <Pluralize singular={'follower'} count={product.follower_count} />
                </div>
            )
        }
    }

    const renderProductName = () => {
        if(nameBlock) {
            return (
                <Row className={`name my-2 d-none d-md-block mx-0 ${nameSize && `name-${nameSize}`}`}>
                    {truncateString(product.name, 35)}
                </Row>
            )
        }
    }

    const renderRating = () => {
        if(showRating) {
            return (
                <div className={'rating'}>
                    {renderRatingStars(product.rating)}
                </div>
            )
        }
    }

    const renderReviewCount = () => {
        if(showReviewCount && !showFlashyStats) {
            return (
                <div className={'stats'}>
                    <Pluralize singular={'review'} count={product.review_count} />
                </div>
            )
        }
    }

    const renderDescription = () => {
        if(showDescription && product?.description) {
            return (
                <div className={`font-16 text-secondary pt-2 sp-line-3 ${showFlashyStats && 'mb-3'}`}>
                    {stripHTMLFromString(product.description)}
                </div>
            )
        }
    }

    const renderCategoryButton = () => {
        if(showCategory && product?.primary_category) {
            return (
                <Row className={'mx-0'}>
                    <Button variant={'light'} size={'sm'} className={'text-truncate text-uppercase d-none d-md-block fw-bolder'}>{product.primary_category}</Button>
                </Row>
            )
        }
    }

    const renderAddToComparison = () => {
        if(comparisonEnabled) {
            return (
                <div className={'position-absolute end-0 top-0 mt-2 me-2'}>
                    <Button
                        variant={'outline-secondary'}
                        size={'sm'}
                        className={'me-1 mt-1'}
                        onClick={(e) => onCompareClickHandler(e, product.id)}>
                        <i className={`fal fa-${activeComparison ? 'minus' : 'plus'} pe-1`}></i>
                        {activeComparison ? 'Remove comparison' : 'Add to comparison'}
                    </Button>
                </div>
            )
        }
    }

    const renderBadge = () => {
        if(showBadge && product?.badge) {
            return (
                <div className={'product-badge'}>
                    <span>
                        <Image src={product.badge.imgsrc} alt={`${COMPANY_NAME} ${product.badge.type}`} width={18} height={18} loading=''></Image>
                        <div className={'text-secondary font-12 fw-bold'}>{product.badge.type}</div>
                        <div className={'text-secondary font-family-primary fw-bolder'} style={{fontSize: '8px'}}>{COMPANY_NAME}</div>
                    </span>
                </div>
            )
        }
    }

    const renderFlashyStats = () => {
        if(showFlashyStats && product?.stats) {
            return (
                <div className={'position-absolute bottom-0 w-100 d-flex align-content-center pe-3 pe-md-5'}>
                    {
                        product.stats.map((stat, i) => {
                            return (
                                <div key={`stat_${product.id}_${i}`} className={`text-${stat.color} w-20 lh-1 pb-2 border-bottom border-${stat.color} border-3`}>
                                    <div className={'fw-bold font-16'}>{stat.count}</div>
                                    <div className={'font-12'}>{stat.title}</div>
                                </div>
                            )
                        })
                    }
                </div>
            )
        }
    }

    const renderProduct = () => {
        if(displayInCard) {
            return (
                <Card className={'product-card p-2 p-sm-3'}>
                    {renderProductContent()}
                </Card>
            )
        } else {
            return (
                <div className={'product-card'}>
                    {renderProductContent()}
                </div>
            )
        }
    }

    const renderProductContent = () => {
        return (
            <div>
                {renderBadge()}
                {renderAddToComparison()}
                <div className={`product-content ${productContentClasses}`}>
                    <div className={'product-logo'}>
                        <Image
                            src={product.thumbnail}
                            alt={product.name}
                            width={logoSize}
                            height={logoSize}
                            style={{ '--product-logo-size': `${logoSize}px`}} />
                    </div>
                    <div className={'product-details'} style={ fitContent ? { width: 'fit-content'} : {}}>
                        {/*Always display the name here if show_basic OR we're on mobile*/}
                        <TooltipTrigger id={`tt_${product.id}`} placement={'left'} content={product.name}>
                            <div className={'product-name'} title={product.name}>
                                {truncateString(product.name, textTruncate)}
                            </div>
                        </TooltipTrigger>
                        {renderDescription()}
                        {renderRating()}
                        {renderReviewCount()}
                        {renderFollowers()}
                    </div>
                </div>
                {renderProductName()}
                {renderCategoryButton()}
                {renderFlashyStats()}
            </div>
        )
    }

    const onProductClickHandler = (e, id) => {
        if(onProductClick) {
            onProductClick(e, id);
        } else {
            window.location.href = product.url;
        }
    }

    const onCompareClickHandler = (e, id) => {
        onCompareClick(e, id);
        setActiveComparison(!activeComparison);
    }

    return (
        <div className={`${colClasses}  product-col`}>
            <ConditionalWrapper
                condition={renderAsLink}
                wrapper={children => <a href={`${product?.url}`} onClick={(e) => onProductClickHandler(e, product.id)}>{children}</a>}
            >
                {renderProduct()}
            </ConditionalWrapper>
        </div>
    )
}

ProductCard.displayName = 'ProductCard';

export default ProductCard;